import { useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { LoaderCircle } from 'lucide-react'
import { queryClient } from '@/lib/queryClient'
import { AppShell } from '@/components/AppShell'
import { LoginPage } from '@/pages/LoginPage'
import { CheckoutPage } from '@/pages/checkout/CheckoutPage'
import { SalesHistoryPage } from '@/pages/sales/SalesHistoryPage'
import { CashPage } from '@/pages/cash/CashPage'
import { CustomersPage } from '@/pages/customers/CustomersPage'
import { ExpensesPage } from '@/pages/expenses/ExpensesPage'
import { InventoryPage } from '@/pages/InventoryPage'
import { ReceiveStockPage } from '@/pages/ReceiveStockPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { ReportsPage } from '@/pages/reports/ReportsPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { HelpPage } from '@/pages/help/HelpPage'
import { PlatformLoginPage } from '@/pages/platform/PlatformLoginPage'
import { PlatformDashboard } from '@/pages/platform/PlatformDashboard'
import { useSession } from '@/stores/session'
import { usePlatformSession } from '@/stores/platformSession'

function RequireSession({ children }: { children: ReactNode }) {
  const session = useSession((state) => state.session)
  const isRestoring = useSession((state) => state.isRestoring)
  const location = useLocation()

  if (isRestoring) {
    // Held until the silent refresh settles, so reloading the page does not bounce a
    // signed-in cashier to the login screen for a frame.
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoaderCircle className="h-6 w-6 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

/** Guards the platform routes, holding them until the silent refresh from `restore()` settles. */
function RequirePlatformSession({ children }: { children: ReactNode }) {
  const session = usePlatformSession((state) => state.session)
  const isRestoring = usePlatformSession((state) => state.isRestoring)

  if (isRestoring) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoaderCircle className="h-6 w-6 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/platform/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  const restore = useSession((state) => state.restore)
  const restorePlatform = usePlatformSession((state) => state.restore)

  useEffect(() => {
    // A tenant and a platform session share the same in-memory token slot in lib/api.ts — only
    // one of the two is ever the active context in a tab, so exactly one of these two refresh
    // attempts is meaningful per route and the other must not run: firing the tenant refresh on
    // a platform page draws a 401 that clears the token slot outright, wiping out the platform
    // token just set by signing in, for a reason that has nothing to do with the platform panel.
    if (window.location.pathname.startsWith('/platform')) {
      void restorePlatform()
    } else {
      void restore()
    }
  }, [restore, restorePlatform])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/platform/login" element={<PlatformLoginPage />} />
          <Route
            path="/platform"
            element={
              <RequirePlatformSession>
                <PlatformDashboard />
              </RequirePlatformSession>
            }
          />

          <Route
            element={
              <RequireSession>
                <AppShell />
              </RequireSession>
            }
          >
            <Route index element={<CheckoutPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="inventory/receive" element={<ReceiveStockPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="sales" element={<SalesHistoryPage />} />
            <Route path="cash" element={<CashPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* No permission gate: everyone needs to be able to look up how something works,
                including the parts of it they are not allowed to do. */}
            <Route path="help" element={<HelpPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
